<template>
    <!-- Tabs -->
    <tabs-generic
    v-if="approvalStatus === 'WARNING'"
    whiteBox
    class="warning-tabs"
    :class="[selectedTab.icon, { mobile: isMobile }]"
    :tabsList="tabsList"
    @clicked="tabClickedHandler"/>

    <transition-group name="common-enter-animation" mode="out-in" appear>
        <div class="step-content" :class="{ mobile: isMobile }" :key="selectedTab.id">
            <!-- Origin Account -->
            <div class="group-wrapper origin-account">
                <divider-generic
                icon="account-balance"
                dividerSize="medium"
                :applyBorder="false"
                :title="t('approvals.pendingSignature.labels.originAccount')"/>
                <!-- Account Number Text Block -->
                <text-block-generic
                applyEllipsis
                :mask="ACCOUNT_NUMBER"
                :params="{
                    label: t('approvals.pendingSignature.labels.account'),
                    content: selectedAccount.accountNumber
                }"/>
            </div>
            <!-- Operations Blocks -->
            <div
            v-for="(block, index) in selectedOperationsBlocks"
            :key="index"
            class="selected-operations-block">
                <div v-if="block.list.length" class="group-wrapper">
                    <div class="block-title">
                        <divider-generic
                        dividerSize="medium"
                        :title="block.title"
                        :icon="block.icon" />
                        <!-- Info Text Block -->
                        <infobox-generic
                        v-if="block.id === 'toSign'"
                        :label="t('approvals.pendingSignature.infoTextBlocks.operationsToSignMessage')" />
                        <!-- Total Amount Text Block -->
                        <text-block-generic
                        applyEllipsis
                        class="total-text-block"
                        :class="{ 'error-block': block.blockError }"
                        :params="{
                            label: block.textBlockLabel,
                            content: amountFormat(block.amount, selectedAccount.currency)
                        }"/>
                    </div>
                    <!-- Select Buttons -->
                    <div v-if="block.hasButtons && block.list.filter(obj => obj.executed).length > 1" class="buttons-wrapper">
                        <!-- Select All -->
                        <button-tertiary
                        :disabled="selectedOperations.length === block.list.length"
                        :label="t('approvals.pendingSignature.step3.buttons.selectAll')"
                        @clicked="selectAll"/>
                        <!-- Clear Selection -->
                        <button-tertiary
                        :disabled="!selectedOperations.length"
                        :label="t('approvals.pendingSignature.step3.buttons.clearAll')"
                        @clicked="emit('clearAllSelected')"/>
                    </div>
                    <!-- Selected Operations List -->
                    <component
                    :is="listComponent"
                    :operationsList="block.list"
                    :activeStep="3"
                    @selectOperation="emit('selectOperation', $event)"
                    @openDetails="emit('openDetails', $event)" />
                </div>
            </div>
            <divider-generic/>
            <!-- Elevated Button -->
            <button-primary
            icon="arrow-right-full"
            iconPosition="right"
            :label="elevatedButtonParams.label"
            @clicked="elevatedButtonParams.emit"/>
        </div>
    </transition-group>
</template>

<script setup>
import TabsGeneric from '@/components/generic/tabs/Generic'
import DividerGeneric from '@/components/generic/dividers/Generic'
import TextBlockGeneric from '@/components/generic/textBlocks/Generic'
import InfoboxGeneric from '@/components/generic/infobox/Generic'
import ButtonPrimary from '@/components/generic/buttons/Primary'
import ButtonTertiary from '@/components/generic/buttons/Tertiary'

/* Vue */
import { ref, computed, defineAsyncComponent } from 'vue'

/* Aux */
import { ACCOUNT_NUMBER } from '@/assets/constants/masks'
import { amountFormat } from '@/assets/scripts/amounts'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/** Props
@selectedAccount {number} - Array with selected account info
@selectedOperations {array} - Array with selected operations
@successfulOperationApprovalsList {array} - Successfully approved operations list
@failedOperationApprovalsList {array} - Failed to approve operations list
@approvalStatus {string} - Status
@isMobile {Boolean}
*/
const props = defineProps({
    selectedAccount: Array,
    selectedOperations: Array,
    successfulOperationApprovalsList: Array,
    failedOperationApprovalsList: Array,
    approvalStatus: String,
    isMobile: Boolean
})

const emit = defineEmits([
    'openDetails',
    'selectAll',
    'clearAllSelected',
    'selectOperation',
    'restart',
    'repeat',
    'selectedStep3Tab'
])

/*************************************************
*                                                *
*               Warning Status Tabs              *
*                                                *
*************************************************/
const tabsList = ref([
	{
        id: 1,
        label: t('approvals.pendingSignature.step3.tabs.error', { total: props.failedOperationApprovalsList.length }),
        icon: 'error',
        list: props.failedOperationApprovalsList,
        selected: true
    },
	{
        id: 2,
        label: t('approvals.pendingSignature.step3.tabs.success', { total: props.successfulOperationApprovalsList.length }),
        icon: 'check-circle',
        list: props.successfulOperationApprovalsList,
        selected: false
    }
])

const selectedTab = computed(() => {
    return tabsList.value.find(tab => tab.selected)
})

const tabClickedHandler = event => {
    tabsList.value.forEach(tab => {
		tab.selected = tab.id === event
	})

    emit('selectedStep3Tab', event)
    emit('clearAllSelected')
}

/*************************************************
*                                                *
*                   List Logic                   *
*                                                *
*************************************************/
const listComponent = computed(() => {
    const listFolder = !props.isMobile ? 'desktop' : 'mobile'
    return defineAsyncComponent(() => import('@/components/features/approvals/pendingSignature/' + listFolder + '/OperationsList'))
})

const listToRender = computed(() => {
    if (props.approvalStatus === 'WARNING') return selectedTab.value.list
    if (props.approvalStatus === 'ERROR' && props.failedOperationApprovalsList.length > 0) return props.failedOperationApprovalsList
    return props.successfulOperationApprovalsList
})

const parsedList = computed(() => {
    return listToRender.value.map(list => ({
        ...list.signedOperationView,
        executed: list.executed,
        successful: list.successful,
        errorMessage: list.errorMessage,
        selected: !!props.selectedOperations.find(selected => selected.signedOperationView.operationId === list.signedOperationView.operationId)
    }))
})

const totalAmountToTransfer = computed(() => {
    return parsedList.value.reduce((acc, obj) => {
        if (obj.amount && obj.amount.amount && obj.executed) acc += obj.amount.amount
        return acc
    }, 0)
})

const totalAmountToSign = computed(() => {
    return parsedList.value.reduce((acc, obj) => {
        if (obj.amount && obj.amount.amount && !obj.executed) acc += obj.amount.amount
        return acc
    }, 0)
})

const selectedOperationsBlocks = computed(() => {
    const selectedErrorTab = (props.approvalStatus === 'WARNING' || props.approvalStatus === 'ERROR') && selectedTab.value.id === 1

    return [
        {
            id: 'toExecute',
            title: selectedErrorTab ? t('approvals.pendingSignature.labels.notExecuted') : t('approvals.pendingSignature.labels.toExecute'),
            icon: 'transfer',
            textBlockLabel: selectedErrorTab ? t('approvals.pendingSignature.labels.totalNotTransfered') : t('approvals.pendingSignature.labels.totalToTransfer'),
            hasButtons: !selectedErrorTab,
            blockError: selectedErrorTab,
            amount: totalAmountToTransfer.value,
            list: parsedList.value.filter(obj => obj.executed)
        },
        {
            id: 'toSign',
            title: selectedErrorTab ? t('approvals.pendingSignature.labels.notSigned') : t('approvals.pendingSignature.labels.toSign'),
            icon: 'pencil-line',
            textBlockLabel: selectedErrorTab ? t('approvals.pendingSignature.labels.totalNotSigned') : t('approvals.pendingSignature.labels.totalToSign'),
            hasButtons: false,
            blockError: selectedErrorTab,
            amount: totalAmountToSign.value,
            list: parsedList.value.filter(obj => !obj.executed)
        }
    ]
})

/*************************************************
*                                                *
*                    Handlers                    *
*                                                *
*************************************************/
const selectAll = () => {
    emit('selectAll', parsedList.value.filter(obj => obj.executed).map(operation => operation.operationId))
}

/*************************************************
*                                                *
*             Restart/Repeat Button              *
*                                                *
*************************************************/
const elevatedButtonParams = computed(() => {
    if (props.approvalStatus === 'WARNING' && selectedTab.value.id === 1) {
        return {
            label: t('approvals.pendingSignature.step3.repeatOperations'),
            emit: () => emit('repeat')
        }
    }

    if (props.approvalStatus === 'ERROR' && props.failedOperationApprovalsList.length > 0) {
        return {
            label: t('approvals.pendingSignature.step3.repeatOperations'),
            emit: () => emit('repeat')
        }
    }

    return {
        label: t('approvals.pendingSignature.step3.aproveOtherOperations'),
        emit: () => emit('restart')
    }
})
</script>

<style lang="scss" scoped>
.warning-tabs {
    margin-bottom: 32px;

    &.error {
        :deep(.tabs-wrapper) {
            .tabs-container {
                svg {
                    .change-color-fill {
                        fill: var(--secondary-dark-1);
                    }
                }
                .selected {
                    color: var(--negative);
                    border-color: var(--negative);

                    svg {
                        .change-color-fill {
                            fill: var(--negative);
                        }
                    }
                }
            }
        }
    }

    &.check-circle {
        :deep(.tabs-wrapper) {
            .tabs-container {
                svg {
                    .change-color-fill {
                        fill: var(--secondary-dark-1);
                    }
                }
                .selected {
                    color: var(--positive);
                    border-color: var(--positive);

                    svg {
                        .change-color-fill {
                            fill: var(--positive);
                        }
                    }
                }
            }
        }
    }

    &.mobile {
        height: 80px;

        :deep(.tabs-wrapper) {
            padding: 0 16px;

            .tabs-container {
                .tab {
                    width: 50%;
                }
            }
        }
    }
}

.step-content {
    @include commonEnteringAnimation;
    .group-wrapper {
        padding: 0;

        display: flex;
        flex-direction: column;

        .divider-generic-wrapper {
            margin-bottom: 24px;
        }

        .block-title {
            width: 100%;

            :deep(.infobox-generic-wrapper) {
                margin-bottom: 24px;
            }
        }

        &.origin-account,
        .block-title {
            padding-bottom: 24px;
        }

        .total-text-block {
            :deep(.content-wrapper) {
                margin-top: 8px;

                .content {
                    @include H3-Regular;
                    line-height: 28px;

                    color: var(--primary);
                }
            }

            &.error-block {
                :deep(.content-wrapper) {
                    .content {
                        color: var(--negative);
                    }
                }
            }
        }

        .buttons-wrapper {
            display: flex;
            gap: 16px;

            margin-bottom: 24px;
        }
    }

    .selected-operations-block {
        &:not(:last-of-type) {
            margin-bottom: 24px;
        }
    }

    &.mobile {
        .buttons-wrapper {
            width: 100%;
        }

        .button-primary {
            width: 100%;
        }
    }
}
</style>
